import { getDayOfWeek, parseTime, type CalendarDate, type Time } from "@internationalized/date"
import type { Location } from "@mmh/types"
import { computed, type ComputedRef, type Ref } from "vue"

interface ServiceFilteringOptions {
    location: ComputedRef<Location | null | undefined> | Ref<Location | null | undefined>
    currentDate: ComputedRef<CalendarDate>
    currentTime: ComputedRef<Time>
    selectedDate: ComputedRef<CalendarDate | undefined>
    selectedDay: ComputedRef<number | undefined>
    selectedOpenClose: ComputedRef<[Time, Time] | null>
}

interface ServiceData {
    service: any
    hours: any
    start: Time
    end: Time
}

interface CompletedAndActiveServices {
    completed: ServiceData[]
    active: ServiceData | null
}

export function useServiceFiltering(options: ServiceFilteringOptions) {
    const {
        location,
        currentDate,
        currentTime,
        selectedDate,
        selectedDay,
        selectedOpenClose
    } = options

    const getServicesForDay = (dayOfWeek: number) => {
        const loc = location.value
        if (!loc) return []
        const services = loc.services
            .filter((service: any) => service.bookable !== false && 
                    service.hours.some((hours: any) => hours.days.includes(dayOfWeek)))
            .map((service: any) => ({
                ...service,
                hours: service.hours.filter((hours: any) => hours.days.includes(dayOfWeek))
            }))
        return services
    }

    const roundToNext30Minutes = (time: Time): Time => {
        const minutes = time.minute
        if (minutes === 0 || minutes === 30) {
            return time
        } else if (minutes <= 30) {
            return time.set({ minute: 30, second: 0, millisecond: 0 })
        } else {
            return time.add({ hours: 1 }).set({ minute: 0, second: 0, millisecond: 0 })
        }
    }

    const isLastServiceOfDay = (): boolean => {
        if (!currentTime.value || !currentDate.value) return false
        
        const todayServices = getServicesForDay(getDayOfWeek(currentDate.value, "en-US"))
        if (!todayServices.length) return false
        
        let latestEndTime: Time | null = null
        let latestServiceName: string | null = null
        
        todayServices.forEach((service: any) => {
            service.hours.forEach((hours: any) => {
                const end = parseTime(hours.times[1])
                if (!latestEndTime || end.compare(latestEndTime) > 0) {
                    latestEndTime = end
                    latestServiceName = service.name
                }
            })
        })
        
        return todayServices.some((service: any) => 
            service.name === latestServiceName &&
            service.hours.some((hours: any) => {
                const start = parseTime(hours.times[0])
                const end = parseTime(hours.times[1])
                return currentTime.value.compare(start) >= 0 && currentTime.value.compare(end) < 0
            })
        )
    }

    const getFirstServiceRange = (dayOfWeek: number): [Time, Time] | null => {
        const services = getServicesForDay(dayOfWeek)
        if (!services.length) return null
        let firstStart: Time | null = null
        let firstEnd: Time | null = null
        services.forEach((service: any) => {
            service.hours.forEach((hours: any) => {
                const start = parseTime(hours.times[0])
                const end = parseTime(hours.times[1])
                if (!firstStart || start.compare(firstStart) < 0) {
                    firstStart = start
                    firstEnd = end
                }
            })
        })
        return firstStart && firstEnd ? [firstStart, firstEnd] : null
    }

    const getCompletedAndActiveServices = (): CompletedAndActiveServices => {
        if (!selectedDay.value || !currentTime.value) {
            return { completed: [], active: null }
        }
        
        const todayServices = getServicesForDay(selectedDay.value)
        const result: CompletedAndActiveServices = { completed: [], active: null }
        todayServices.forEach((service: any) => {
            service.hours.forEach((hours: any) => {
                const start = parseTime(hours.times[0])
                const end = parseTime(hours.times[1])
                const serviceData: ServiceData = { service, hours, start, end }
                
                if (currentTime.value.compare(end) >= 0) {
                    result.completed.push(serviceData)
                } else if (currentTime.value.compare(start) >= 0 && currentTime.value.compare(end) < 0) {
                    result.active = serviceData
                }
            })
        })
        
        return result
    }

    const getFilteredTimeOptions = (): Time[] => {
        const options: Time[] = []
        if (!selectedOpenClose.value || !selectedDate.value) {
            return options
        }

        const [open, close] = selectedOpenClose.value
        let time = open
        
        if (selectedDate.value.compare(currentDate.value) === 0) {
            const { completed, active } = getCompletedAndActiveServices()
                
            if (currentTime.value.compare(time) > 0) {
                time = roundToNext30Minutes(currentTime.value)
            } else {
                time = roundToNext30Minutes(time)
            }

            if (selectedDay.value !== undefined) {
                const firstRange = getFirstServiceRange(selectedDay.value)
                if (firstRange && currentTime.value.compare(firstRange[0]) < 0 && time.compare(firstRange[1]) <= 0) {
                    time = roundToNext30Minutes(firstRange[1])
                }
            }
                
            while (time.compare(close) < 0) {
                let shouldInclude = true
                    
                for (const completedService of completed) {
                    if (time.compare(completedService.start) >= 0 && time.compare(completedService.end) < 0) {
                        shouldInclude = false
                        break
                    }
                }
                    
                if (shouldInclude && active) {
                    if (time.compare(active.start) >= 0 && time.compare(active.end) < 0) {
                        shouldInclude = false
                    }
                }
                    
                if (shouldInclude) {
                    options.push(time)
                }
                    
                time = time.add({ minutes: 30 })
            }
        } else {
            time = roundToNext30Minutes(time)
            const isTomorrow = selectedDate.value.compare(currentDate.value.add({ days: 1 })) === 0
            const afterEight = currentTime.value.compare(parseTime("20:00")) > 0
                
            // If it's tomorrow and after 8pm, skip the entire first service period
            if (isTomorrow && afterEight && selectedDay.value !== undefined) {
                const firstRange = getFirstServiceRange(selectedDay.value)
                if (firstRange && time.compare(firstRange[1]) <= 0) {
                    time = roundToNext30Minutes(firstRange[1])
                }
            }

            while (time.compare(close) < 0) {
                options.push(time)
                time = time.add({ minutes: 30 })
            }
        }
        
        return options
    }

    const getCalendarMinDate = (): CalendarDate => {
        if (isLastServiceOfDay()) {
            return currentDate.value.add({ days: 1 })
        }
        return currentDate.value
    }

    const timeOptions = computed(() => getFilteredTimeOptions())
    const calendarMinDate = computed(() => getCalendarMinDate())

    return {
        timeOptions,
        calendarMinDate
    }
}
